import styled from 'styled-components';
import { RepeatKind, SongListSchemaEntry } from '../../../../../common/src/model.ts';
import { getRepeatKind, isHymnOfTheMonthRow } from '../../../../../common/src/repeating_rows.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';
import CalendarDotsIcon from '../../../assets/calendar-dots.svg?react';
import CalendarMonthIcon from '../../../assets/calendar-month.svg?react';
import InfinityIcon from '../../../assets/infinity.svg?react';
import RotateSundaysIcon from '../../../assets/rotate-sundays.svg?react';
import StarFilledIcon from '../../../assets/star-filled.svg?react';

// How a church liturgy row's repeat kind (issue #4170) reads on screen: its label, its icon in the row menu, and the
// glyphs beside the type icon in the row header.

export function getRepeatKindLabel(kind: RepeatKind): string {
  switch (kind) {
    case RepeatKind.DoesNotRepeat:
      return 'Does not repeat';
    case RepeatKind.SameAllMonth:
      return 'Same all month';
    case RepeatKind.SameEverySunday:
      return 'Same every Sunday';
    case RepeatKind.BySundayOfMonth:
      return 'By Sunday of the month';
    default:
      ensureUnreachable(kind);
  }
}

function getRepeatKindSvg(kind: RepeatKind) {
  switch (kind) {
    case RepeatKind.DoesNotRepeat:
      return <CalendarDotsIcon aria-hidden={true} />;
    case RepeatKind.SameAllMonth:
      return <CalendarMonthIcon aria-hidden={true} />;
    case RepeatKind.SameEverySunday:
      return <InfinityIcon aria-hidden={true} />;
    case RepeatKind.BySundayOfMonth:
      return <RotateSundaysIcon aria-hidden={true} />;
    default:
      ensureUnreachable(kind);
  }
}

// the kind's icon at the size of the row type icons, for menu entries
export function RepeatKindIcon({ kind }: { kind: RepeatKind }) {
  return <IconWrapper aria-hidden={true}>{getRepeatKindSvg(kind)}</IconWrapper>;
}

// the gold star of Show as Hymn of the Month, at the size of the row type icons
export function HymnOfTheMonthStarIcon() {
  return (
    <IconWrapper aria-hidden={true} $color="var(--color-gold-400)">
      <StarFilledIcon />
    </IconWrapper>
  );
}

// the row header glyphs of a repeating row: the star when it is a Hymn of the Month row (isHymnOfTheMonthRow), then the
// kind's glyph; nothing for a Does-not-repeat row
export function RepeatGlyphs({ row }: { row: SongListSchemaEntry | undefined }) {
  const kind = getRepeatKind(row);
  if (kind === RepeatKind.DoesNotRepeat) {
    return null;
  }
  const isStarred = isHymnOfTheMonthRow(row);
  return (
    <GlyphsWrapper>
      {isStarred && (
        <IconWrapper role="img" aria-label="Shown as Hymn of the Month" $color="var(--color-gold-400)">
          <StarFilledIcon aria-hidden={true} />
        </IconWrapper>
      )}
      <IconWrapper role="img" aria-label={getRepeatKindLabel(kind)}>
        {getRepeatKindSvg(kind)}
      </IconWrapper>
    </GlyphsWrapper>
  );
}

// a 15px icon in a box the size of the row type icons, for menu entries and the row header glyphs alike
const IconWrapper = styled.span<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 15px;
  height: 18px;
  color: ${(props) => props.$color ?? 'var(--color-text)'};
  fill: currentColor;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const GlyphsWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;
